import type { ReactElement, ReactNode } from "react";

import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useValidateAuth } from "@/middleware/useValidateAuth";
import { Spinner } from "@/components/ui/v2/spinner";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { useInitSocket } from "../services/websocket/useInitSocket";
import { useProactiveTokenRefresh } from "@/hooks/useProactiveTokenRefresh";
import { useGetNotificationSettingsQuery } from "@/services/(my-space)/notificationApi";
import { useGetSidebarPersmissionsQuery } from "@/services/(my-space)/userApi";
import { useSelector } from "react-redux";
import { selectUser } from "@/store/slices/user.slice";
import { STANDALONE_PATHS } from "@/routes/common/standalone-paths";
import { SUBSCRIPTION_STATUS_ENUM } from "@/utils/enums/subscription.enum";
import { redirectToMap } from "@/utils";

const INACTIVE_SUBSCRIPTION_STATUSES = [SUBSCRIPTION_STATUS_ENUM.CANCELED, SUBSCRIPTION_STATUS_ENUM.UNPAID, SUBSCRIPTION_STATUS_ENUM.NO_SUBSCRIPTION];

const hasInactiveSubscription = (user: ReturnType<typeof selectUser>["user"]): boolean => {
  if (!user?.company) return false;

  const subscription = user.company.companySubscription;
  return !subscription || INACTIVE_SUBSCRIPTION_STATUSES.includes(subscription.status);
};

const SocketProvider = ({ children }: { children: ReactNode }) => {
  useInitSocket();
  useProactiveTokenRefresh();
  // Load notification preferences once per authenticated session so app-wide
  // consumers (e.g. read-aloud in the websocket listener) have them in the store
  // even when the user never opens the settings page.
  useGetNotificationSettingsQuery();
  return children;
};

export const RequireAuthMiddleware = ({ children }: { children: ReactElement }) => {
  const { isChecking, isAuthenticated } = useValidateAuth();
  const location = useLocation();
  const { user } = useSelector(selectUser);

  if (isChecking) return <Spinner wrapperClassName="h-screen" className="size-10" />;
  if (!isAuthenticated) return <Navigate to="/auth/login" state={{ from: location }} replace />;
  if (hasInactiveSubscription(user)) return <Navigate to={user?.hasSubscriptionPermission ? STANDALONE_PATHS.SUBSCRIPTION_PLAN : STANDALONE_PATHS.SUBSCRIPTION_EXPIRED} replace />;
  return <SocketProvider>{children}</SocketProvider>;
};

// Gates the company side. A user may reach `/company/*` only if they are a
// company super admin or their role grants at least one company-level sidebar
// link. Anyone else (e.g. an employee) is redirected to My Space — this blocks
// direct URL navigation to any company route, not just hiding the nav links.
export const RequireCompanyAccessMiddleware = ({ children }: { children: ReactElement }) => {
  const { user } = useSelector(selectUser);
  const { data: sidebarLinks, isLoading } = useGetSidebarPersmissionsQuery();

  const hasCompanyAccess =
    (user?.isCompanySuperAdmin ?? false) ||
    (sidebarLinks?.data?.company?.length ?? 0) > 0;

  if (isLoading) return <Spinner wrapperClassName="h-screen" className="size-10" />;
  if (!hasCompanyAccess) return <Navigate to="/" replace />;
  return children;
};

// Gates the My Space Time Attendance area. A user may reach `/time-attendance/*`
// only if their role grants individual-level Time Attendance "view" (company
// super admins always pass). Anyone else — e.g. an employee whose role has the
// Time Attendance permission unchecked — is redirected Home. This blocks direct
// URL navigation to any Time Attendance route, not just hiding the nav link.
// Renders <Outlet/> so it can be mounted as the route node's Component.
export const RequireTimeAttendanceAccess = () => {
  const { can, isLoading } = usePermissions();

  if (isLoading) return <Spinner wrapperClassName="h-screen" className="size-10" />;
  if (!can(PLATFORM_SECTION_ENUM.TIME_ATTENDANCE, "view")) return <Navigate to="/" replace />;
  return <Outlet />;
};

export const RequireInactiveSubscriptionMiddleware = ({ children }: { children: ReactElement }) => {
  const { isChecking, isAuthenticated } = useValidateAuth();
  const location = useLocation();
  const { user } = useSelector(selectUser);

  if (isChecking) return <Spinner wrapperClassName="h-screen" className="size-10" />;
  if (!isAuthenticated) return <Navigate to="/auth/login" state={{ from: location }} replace />;
  if (!hasInactiveSubscription(user)) return <Navigate to={redirectToMap(user?.redirectTo ?? undefined)} replace />;
  return children;
};
